import { incidentR } from "./data";
import {
  getMaterials,
  getDirectMaterialAlbedo,
  getDiffuseMaterialAlbedo,
  getAllCellIds,
  getSurfaceArea,
  getSurfaceDirection,
} from "./utils";
import {
  calcVF_parallel,
  calcVF_perpendicular_ftos,
  calcVF_perpendicular_stof,
} from "./VF";

export const Gebhart = (
  cellProps,
  idSet,
  oppSet,
  nCells,
  cellWidth,
  streetWidth,
) => {
  let o = { gorge: {} };
  // let o = {};

  idSet.forEach((cellId) => {
    o[cellId] = {};
    const sComp = getMaterials(cellProps, cellId);
    const cellIndex = idSet.indexOf(cellId);

    o[cellId]["gorge"] =
      calcVF_perpendicular_ftos(
        cellId,
        cellIndex,
        cellProps,
        nCells,
        cellWidth,
        streetWidth,
      ) * oneMinusReflectance(sComp);

    o["gorge"][cellId] =
      calcVF_perpendicular_stof(
        cellId,
        cellIndex,
        cellProps,
        nCells,
        cellWidth,
        streetWidth,
      ) * oneMinusReflectance(cellProps["gorge"].materials);

    for (let i = 0; i < oppSet.length; i++) {
      let vf = calcVF_parallel(
        i,
        cellIndex,
        cellId,
        oppSet[i],
        cellProps,
        cellWidth,
        streetWidth,
      );

      o[cellId][oppSet[i]] = vf * oneMinusReflectance(sComp);
    }
  });

  oppSet.forEach((cellId) => {
    o[cellId] = {};
    const sComp = getMaterials(cellProps, cellId);
    const cellIndex = oppSet.indexOf(cellId);

    o[cellId]["gorge"] =
      calcVF_perpendicular_ftos(
        cellId,
        cellIndex,
        cellProps,
        nCells,
        cellWidth,
        streetWidth,
      ) * oneMinusReflectance(sComp);

    o["gorge"][cellId] =
      calcVF_perpendicular_stof(
        cellId,
        cellIndex,
        cellProps,
        nCells,
        cellWidth,
        streetWidth,
      ) * oneMinusReflectance(cellProps["gorge"].materials);

    for (let i = 0; i < idSet.length; i++) {
      let vf = calcVF_parallel(
        i,
        cellIndex,
        cellId,
        idSet[i],
        cellProps,
        cellWidth,
        streetWidth,
      );

      o[cellId][idSet[i]] = vf * oneMinusReflectance(sComp);
    }
  });

  return o;
};

const oneMinusReflectance = (sComp) => {
  return Object.getOwnPropertyNames(sComp).reduce(
    (acc, cur) => acc + (1 - getDirectMaterialAlbedo(cur)) * sComp[cur],
    0,
  );
};

const getRadiance = (
  direction = "NE (SE)",
  time = "0730",
  type,
  day = "Jun21",
) => incidentR[day][direction][type][time];

const getDirectRadiance = (sDir, time, date) =>
  getRadiance(sDir, time, "Direct", date);

const getDiffuseRadiance = (sDir, time, date) =>
  getRadiance(sDir, time, "Diffuse", date);

// sDir: Direction of the surface as string
// sComp: Material composition of a surface as object
// time: Time of day passed as string
// date: Date passed as string
export const oneMinusReflectance2 = (sDir, sComp, time, date = "Jun21") => {
  return Object.getOwnPropertyNames(sComp).reduce(
    (acc, currentMaterial) =>
      acc +
      getDirectMaterialAlbedo(currentMaterial) *
        getDirectRadiance(sDir, time, date) *
        sComp[currentMaterial] +
      getDiffuseMaterialAlbedo(currentMaterial) *
        getDiffuseRadiance(sDir, time, date) *
        sComp[currentMaterial],
    0,
  );
};

// sDir: Direction of the surface as string
// sComp: Material composition of a surface as object
// sArea: Area of the surface as a float
// time: Time of day passed as string
// date: Date passed as string
export const ABySurface = (directRadiance, sComp, sArea) => {
  return Object.getOwnPropertyNames(sComp).reduce((acc, currentMaterial) => {
    return (
      acc +
      (1 - getDirectMaterialAlbedo(currentMaterial)) *
        directRadiance *
        sArea *
        sComp[currentMaterial]
    );
  }, 0);
};

export const A = (cellParams, time = "0730", date = "Jun21") => {
  const allCellIds = getAllCellIds(cellParams);
  const a = allCellIds.reduce(
    (acc, curCell) =>
      acc +
      ABySurface(
        getDirectRadiance(getSurfaceDirection(cellParams, curCell), time, date),
        getMaterials(cellParams, curCell),
        getSurfaceArea(cellParams, curCell),
      ),
    0,
  );

  return a;
};

export const BBySurface = (diffuseRadiance, sComp, sArea) => {
  return Object.getOwnPropertyNames(sComp).reduce((acc, currentMaterial) => {
    return (
      acc +
      (1 - getDirectMaterialAlbedo(currentMaterial)) *
        diffuseRadiance *
        sArea *
        sComp[currentMaterial]
    );
  }, 0);
};

export const B = (cellParams, time = "0730", date = "Jun21") => {
  const allCellIds = getAllCellIds(cellParams);

  const b = allCellIds.reduce(
    (acc, curCell) =>
      acc +
      BBySurface(
        getDiffuseRadiance(
          getSurfaceDirection(cellParams, curCell),
          time,
          date,
        ),
        getMaterials(cellParams, curCell),
        getSurfaceArea(cellParams, curCell),
      ),
    0,
  );

  return b;
};

export const C = (gebhart, cellParams, idSet, oppSet, time, date) => {
  let o = { gorge: {} };
  // let o = {};

  idSet.forEach((cellId) => {
    o[cellId] = {};
    for (let i = 0; i < idSet.length; i++) {
      let g = gebhart[cellId][oppSet[i]];
      o[cellId][oppSet[i]] =
        g *
        getSurfaceArea(cellParams, cellId) *
        oneMinusReflectance2(
          getSurfaceDirection(cellParams, cellId),
          getMaterials(cellParams, cellId),
          time,
          date,
        );
      o[cellId]["gorge"] =
        gebhart[cellId]["gorge"] *
        getSurfaceArea(cellParams, cellId) *
        oneMinusReflectance2(
          getSurfaceDirection(cellParams, cellId),
          getMaterials(cellParams, cellId),
          time,
          date,
        );
      o["gorge"][cellId] =
        gebhart["gorge"][cellId] *
        getSurfaceArea(cellParams, "gorge") *
        oneMinusReflectance2(
          getSurfaceDirection(cellParams, "gorge"),
          getMaterials(cellParams, "gorge"),
          time,
          date,
        );
    }
  });

  oppSet.forEach((cellId) => {
    o[cellId] = {};
    for (let i = 0; i < oppSet.length; i++) {
      let g = gebhart[cellId][idSet[i]];
      o[cellId][idSet[i]] =
        g *
        getSurfaceArea(cellParams, cellId) *
        oneMinusReflectance2(
          getSurfaceDirection(cellParams, cellId),
          getMaterials(cellParams, cellId),
          time,
          date,
        );
      o[cellId]["gorge"] =
        gebhart[cellId]["gorge"] *
        getSurfaceArea(cellParams, cellId) *
        oneMinusReflectance2(
          getSurfaceDirection(cellParams, cellId),
          getMaterials(cellParams, cellId),
          time,
          date,
        );
      o["gorge"][cellId] =
        gebhart["gorge"][cellId] *
        getSurfaceArea(cellParams, "gorge") *
        oneMinusReflectance2(
          getSurfaceDirection(cellParams, "gorge"),
          getMaterials(cellParams, "gorge"),
          time,
          date,
        );
    }
  });

  return o;
};

export const CTotal = (g, cellParams, idSet1, idSet2, time, date = "Jun21") => {
  let s = 0.0;

  const c = C(g, cellParams, idSet1, idSet2, time, date);

  for (let i = 0; i < idSet1.length; i++) {
    for (let j = 0; j < idSet2.length; j++) {
      s = s + c[idSet1[i]][idSet2[j]];
    }
    s = s + c[idSet1[i]]["gorge"] + c["gorge"][idSet1[i]];
  }

  for (let i = 0; i < idSet2.length; i++) {
    for (let j = 0; j < idSet1.length; j++) {
      s = s + c[idSet2[i]][idSet1[j]];
    }
    s = s + c[idSet2[i]]["gorge"] + c["gorge"][idSet2[i]];
  }

  return s;
};

export const D = (cellParams, time = "0730", date = "Jun21") => {
  const allCellIds = getAllCellIds(cellParams);

  return allCellIds.reduce((acc, cellId) => {
    return (
      acc +
      (getDirectRadiance(getSurfaceDirection(cellParams, cellId), time, date) +
        getDiffuseRadiance(
          getSurfaceDirection(cellParams, cellId),
          time,
          date,
        )) *
        getSurfaceArea(cellParams, cellId)
    );
  }, 0);
};

export const TotalCalculatedAlbedo = (A, B, C, D) => 1 - (A + B + C) / D;
